import { Box, BoxProps, SxProps, Tab, TabProps, Tabs, Theme } from '@mui/material';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import { AttachmentsGridProps } from 'components/ui/file-uploader/AttachmentsGrid';
import EntityHistory, { EntityHistoryProps } from 'features/dashboard/widgets/EntityHistory';
import { t } from 'i18next';
import React, { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AutoDocumentConfig, DocumentsAttachmentsTab } from './DocumentsAttachmentsTab';

/**
 * Rappresenta i possibili stili visivi/semantici di una tab.
 * Facilmente estendibile in futuro (es. 'danger', 'warning').
 */
export type TabVariant = 'standard' | 'admin';

/**
 * Represents a single tab item configuration.
 */
export interface TabItem {
  /** The text label displayed on the tab header. */
  label: string;
  /** Optional icon element displayed alongside or above the text label. */
  icon?: React.ReactElement;
  /** The component or content layout to render when this tab is selected. */
  component: ReactNode;
  /** If true, removes the default padding (p: 3) from the tab panel container. */
  disablePadding?: boolean;
  /** Custom native MUI TabProps to forward directly to the underlying <Tab /> component. */
  tabProps?: Omit<TabProps, 'label' | 'icon' | 'id' | 'aria-controls'>;
  /** Custom native MUI BoxProps to forward directly to the underlying panel wrapper container. */
  panelWrapperProps?: BoxProps;
  /** If false or undefined, the tab will be completely hidden from headers and panels. */
  visible?: boolean;
  /** Definisce lo scopo e lo stile visivo della tab. Di default è 'standard'. */
  variant?: TabVariant;
}

/**
 * Component properties for EnhancedTabs, extending native MUI Tabs props.
 */
interface EnhancedTabsProps
  extends Omit<React.ComponentProps<typeof Tabs>, 'value' | 'onChange' | 'children' | 'sx'> {
  /** Array of tab objects containing headers configuration and panel components. */
  tabs: TabItem[];
  /** Optional prefix string used to generate unique HTML id and aria attributes. Default is 'custom-tabs'. */
  prefixId?: string;
  /** Optional override for the query parameter key saved in the URL. Defaults to `${prefixId}_tab`. */
  urlParamKey?: string;
  /** Native MUI BoxProps forwarded to the outermost layout wrapper. */
  containerProps?: BoxProps;
  /** Native MUI BoxProps forwarded to the wrapper surrounding the tab headers bar. */
  tabsHeaderWrapperProps?: BoxProps;
  /** Custom native MUI SxProps style objects applied directly to the underlying <Tabs /> component. */
  sx?: SxProps<Theme>;
  /** Optional callback fired when the active tab state changes. */
  onChange?: (event: React.SyntheticEvent, newValue: number) => void;

  documentConfig?: AutoDocumentConfig;
  historyConfig?: EntityHistoryProps;
  attachmentsConfig?: AttachmentsGridProps & { visible?: boolean };
}

/**
 * Properties for the internal GenericTabPanel component, extending native MUI BoxProps.
 */
interface TabPanelProps extends BoxProps {
  /** The content layout to display inside the panel container. */
  children?: ReactNode;
  /** The fixed unique index assigned to this specific panel. */
  index: number;
  /** The active selected tab index shared by the parent layout state. */
  value: number;
  /** Prefix string used to match HTML id and aria-labelledby linkages. */
  prefixId: string;
  /** If true, skips injecting the default spacing padding container wrapper. */
  disablePadding?: boolean;
}

/**
 * Internal helper component that manages individual tab visibility and content layout.
 */
const GenericTabPanel = ({
  children,
  value,
  index,
  prefixId,
  disablePadding = false,
  ...props
}: TabPanelProps) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`${prefixId}-tabpanel-${index}`}
    aria-labelledby={`${prefixId}-tab-${index}`}
    sx={{ width: '100%', ...props.sx }}
    {...props}>
    {value === index && <Box sx={disablePadding ? undefined : { p: 3 }}>{children}</Box>}
  </Box>
);

/**
 * A highly customizable tabs layout component integrated with react-router-dom URL search parameters.
 * Automatically synchronizes the active tab state with the browser query string for bookmarking and deep-linking.
 *
 * @example
 * ```tsx
 * const tabs = [
 *   { label: 'Overview', component: <OverviewComponent /> },
 *   { label: 'Settings', component: <SettingsComponent />, disablePadding: true }
 * ];
 *
 * <EnhancedTabs tabs={tabs} variant="scrollable" color="secondary" />
 * ```
 */
export const EnhancedTabs = ({
  tabs,
  prefixId = 'edit-view',
  urlParamKey,
  containerProps,
  tabsHeaderWrapperProps,
  documentConfig,
  historyConfig,
  attachmentsConfig,
  ...restTabsProps
}: EnhancedTabsProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const superAdmin = useIsTenantAdministrator();

  const computedTabs = [...tabs];

  const hasDocuments = documentConfig?.entityId && documentConfig?.visible !== false;
  const hasAttachments = attachmentsConfig?.entity && attachmentsConfig?.visible !== false;

  if (hasDocuments || hasAttachments) {
    computedTabs.push({
      label: t('global.tabs.documents'),
      component: (
        <DocumentsAttachmentsTab
          documentConfig={documentConfig}
          attachmentsConfig={attachmentsConfig}
        />
      )
    });
  }

  if (historyConfig && historyConfig.entityId) {
    const { service, entityId } = historyConfig;

    computedTabs.push({
      label: t(`global.tabs.history`),
      visible: superAdmin,
      variant: 'admin',
      component: <EntityHistory service={service} entityId={entityId} />
    });
  }

  const visibleTabs = computedTabs.filter((tab) => tab.visible !== false);

  const paramKey = urlParamKey || `${prefixId}_tab`;
  const urlTabValue = parseInt(searchParams.get(paramKey) || '0', 10);

  const activeTab = urlTabValue >= 0 && urlTabValue < visibleTabs.length ? urlTabValue : 0;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSearchParams(
      (prev) => {
        prev.set(paramKey, String(newValue));
        return prev;
      },
      { replace: true }
    );

    if (restTabsProps.onChange) {
      restTabsProps.onChange(event, newValue);
    }
  };

  return (
    <Box sx={{ width: '100%', ...containerProps?.sx }} {...containerProps}>
      <Box
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, ...tabsHeaderWrapperProps?.sx }}
        {...tabsHeaderWrapperProps}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          scrollButtons="auto"
          selectionFollowsFocus
          variant="fullWidth"
          {...restTabsProps}>
          {visibleTabs.map((tab, idx) => (
            <Tab
              key={`${prefixId}-tab-${idx}`}
              icon={tab.icon}
              label={tab.label}
              id={`${prefixId}-tab-${idx}`}
              aria-controls={`${prefixId}-tabpanel-${idx}`}
              {...tab.tabProps}
              {...tab.tabProps}
              // Iniettiamo la classe condizionale: MUI applicherà gli stili di globalComponents in automatico
              className={`${tab.tabProps?.className || ''} ${
                tab.variant === 'admin' ? 'MuiTab-admin' : ''
              }`.trim()}
              // Manteniamo sx solo per elaborare in modo sicuro configurazioni spot esterne
              sx={tab.tabProps?.sx}
            />
          ))}
        </Tabs>
      </Box>

      {visibleTabs.map((tab, idx) => (
        <GenericTabPanel
          key={`${prefixId}-panel-${idx}`}
          value={activeTab}
          index={idx}
          prefixId={prefixId}
          disablePadding={tab.disablePadding}
          {...tab.panelWrapperProps}>
          {tab.component}
        </GenericTabPanel>
      ))}
    </Box>
  );
};
